// TipTap extension that auto-converts raw LaTeX syntax into editor nodes.
// Handles: $...$, $$...$$, \[...\], \section{}, \textbf{}, \begin{...}, etc.

import { Extension, InputRule } from "@tiptap/core";

export const LatexInputRules = Extension.create({
    name: "latexInputRules",

    addInputRules() {
        return [
            // ── Dollar-sign math ─────────────────────────────────────────

            // Display math: $$...$$ → displayMath node (pre-filled)
            new InputRule({
                find: /\$\$([^$]+)\$\$$/,
                handler: ({ state, range, match }) => {
                    const { tr, schema } = state;
                    const latex = match[1].trim();
                    const nodeType = schema.nodes.displayMath;
                    if (!nodeType) return null;

                    const node = nodeType.create({ latex });
                    const $from = state.doc.resolve(range.from);
                    const textBefore = state.doc.textBetween(
                        $from.start($from.depth),
                        range.from,
                    );

                    if (textBefore.trim() === "") {
                        // Replace entire paragraph with the display math block
                        const paraStart = $from.start($from.depth) - 1;
                        const paraEnd = $from.end($from.depth) + 1;
                        tr.replaceWith(paraStart, paraEnd, node);
                    } else {
                        tr.replaceWith(range.from, range.to, node);
                    }
                },
            }),

            // Inline math: $...$ → inlineMath node (pre-filled)
            // Negative lookbehind prevents matching inside $$...$$
            new InputRule({
                find: /(?<!\$)\$([^$]+)\$$/,
                handler: ({ state, range, match }) => {
                    const { tr, schema } = state;
                    const latex = match[1].trim();
                    const nodeType = schema.nodes.inlineMath;
                    if (!nodeType) return null;

                    const id = String(Date.now());
                    const node = nodeType.create({ latex, instanceId: id });
                    const space = schema.text(" ");
                    tr.replaceWith(range.from, range.to, [space, node, space]);
                },
            }),

            // Display math: \[...\] → displayMath node (pre-filled)
            new InputRule({
                find: /\\\[(.+)\\\]$/,
                handler: ({ state, range, match }) => {
                    const { tr, schema } = state;
                    const latex = match[1].trim();
                    const nodeType = schema.nodes.displayMath;
                    if (!nodeType) return null;

                    const node = nodeType.create({ latex });
                    const $from = state.doc.resolve(range.from);
                    const textBefore = state.doc.textBetween(
                        $from.start($from.depth),
                        range.from,
                    );

                    if (textBefore.trim() === "") {
                        const paraStart = $from.start($from.depth) - 1;
                        const paraEnd = $from.end($from.depth) + 1;
                        tr.replaceWith(paraStart, paraEnd, node);
                    } else {
                        tr.replaceWith(range.from, range.to, node);
                    }
                },
            }),

            // ── Section commands (longest first) ─────────────────────────

            // \subsubsection{Text} → heading level 3
            new InputRule({
                find: /\\subsubsection\{([^}]*)\}$/,
                handler: ({ state, range, match }) => {
                    sectionHandler(state, range, match, 3);
                },
            }),

            // \subsection{Text} → heading level 2
            new InputRule({
                find: /\\subsection\{([^}]*)\}$/,
                handler: ({ state, range, match }) => {
                    sectionHandler(state, range, match, 2);
                },
            }),

            // \section{Text} → heading level 1
            new InputRule({
                find: /\\section\{([^}]*)\}$/,
                handler: ({ state, range, match }) => {
                    sectionHandler(state, range, match, 1);
                },
            }),

            // ── Inline formatting ────────────────────────────────────────

            // \textbf{Text} → bold text
            new InputRule({
                find: /\\textbf\{([^}]+)\}$/,
                handler: ({ state, range, match }) => {
                    inlineMarkHandler(state, range, match, "bold");
                },
            }),

            // \textit{Text} → italic text
            new InputRule({
                find: /\\textit\{([^}]+)\}$/,
                handler: ({ state, range, match }) => {
                    inlineMarkHandler(state, range, match, "italic");
                },
            }),

            // \emph{Text} → italic text
            new InputRule({
                find: /\\emph\{([^}]+)\}$/,
                handler: ({ state, range, match }) => {
                    inlineMarkHandler(state, range, match, "italic");
                },
            }),

            // \underline{Text} → underline text (if mark exists)
            new InputRule({
                find: /\\underline\{([^}]+)\}$/,
                handler: ({ state, range, match }) => {
                    inlineMarkHandler(state, range, match, "underline");
                },
            }),

            // \texttt{Text} → code text
            new InputRule({
                find: /\\texttt\{([^}]+)\}$/,
                handler: ({ state, range, match }) => {
                    inlineMarkHandler(state, range, match, "code");
                },
            }),

            // ── Block environments: \begin{...} ─────────────────────────

            // \begin{theorem|lemma|...} → theorem block
            new InputRule({
                find: /\\begin\{(theorem|lemma|proposition|corollary|definition|remark|example|proof|exercise)\}$/,
                handler: ({ state, range, match }) => {
                    const { tr, schema } = state;
                    const envType = match[1];
                    const nodeType = schema.nodes.theoremBlock;
                    if (!nodeType) return null;

                    const para = schema.nodes.paragraph.create();
                    const node = nodeType.create(
                        { envType, envName: "" },
                        [para],
                    );
                    replaceBlockCommand(state, tr, range, node);
                },
            }),

            // \begin{itemize} → bullet list
            new InputRule({
                find: /\\begin\{itemize\}$/,
                handler: ({ state, range }) => {
                    const { tr, schema } = state;
                    const nodeType = schema.nodes.bulletList;
                    if (!nodeType) return null;

                    const node = nodeType.create({}, [
                        schema.nodes.listItem.create({}, [
                            schema.nodes.paragraph.create(),
                        ]),
                    ]);
                    replaceBlockCommand(state, tr, range, node);
                },
            }),

            // \begin{enumerate} → ordered list
            new InputRule({
                find: /\\begin\{enumerate\}$/,
                handler: ({ state, range }) => {
                    const { tr, schema } = state;
                    const nodeType = schema.nodes.orderedList;
                    if (!nodeType) return null;

                    const node = nodeType.create({}, [
                        schema.nodes.listItem.create({}, [
                            schema.nodes.paragraph.create(),
                        ]),
                    ]);
                    replaceBlockCommand(state, tr, range, node);
                },
            }),

            // \begin{abstract} → docAbstract
            new InputRule({
                find: /\\begin\{abstract\}$/,
                handler: ({ state, range }) => {
                    const { tr, schema } = state;
                    const nodeType = schema.nodes.docAbstract;
                    if (!nodeType) return null;

                    const node = nodeType.create({}, [
                        schema.nodes.paragraph.create(),
                    ]);
                    replaceBlockCommand(state, tr, range, node);
                },
            }),

            // \begin{equation} → numbered equation (empty, opens in edit mode)
            new InputRule({
                find: /\\begin\{equation\}$/,
                handler: ({ state, range }) => {
                    const { tr, schema } = state;
                    const nodeType = schema.nodes.numberedEquation;
                    if (!nodeType) return null;

                    const node = nodeType.create({ latex: "" });
                    replaceBlockCommand(state, tr, range, node);
                },
            }),

            // \begin{equation*} → display math (empty, opens in edit mode)
            new InputRule({
                find: /\\begin\{equation\*\}$/,
                handler: ({ state, range }) => {
                    const { tr, schema } = state;
                    const nodeType = schema.nodes.displayMath;
                    if (!nodeType) return null;

                    const node = nodeType.create({ latex: "" });
                    replaceBlockCommand(state, tr, range, node);
                },
            }),

            // \begin{align} or \begin{aligned} → display math (empty)
            new InputRule({
                find: /\\begin\{align(?:ed)?\}$/,
                handler: ({ state, range }) => {
                    const { tr, schema } = state;
                    const nodeType = schema.nodes.displayMath;
                    if (!nodeType) return null;

                    const node = nodeType.create({ latex: "" });
                    replaceBlockCommand(state, tr, range, node);
                },
            }),

            // ── Other commands ───────────────────────────────────────────

            // \tableofcontents → ToC node
            new InputRule({
                find: /\\tableofcontents$/,
                handler: ({ state, range }) => {
                    const { tr, schema } = state;
                    const nodeType = schema.nodes.docToc;
                    if (!nodeType) return null;

                    const node = nodeType.create();
                    replaceBlockCommand(state, tr, range, node);
                },
            }),

            // \title{Text} → docTitle node
            new InputRule({
                find: /\\title\{([^}]*)\}$/,
                handler: ({ state, range, match }) => {
                    const { tr, schema } = state;
                    const text = match[1];
                    const nodeType = schema.nodes.docTitle;
                    if (!nodeType) return null;

                    const content = text ? [schema.text(text)] : [];
                    const node = nodeType.create({}, content);
                    replaceBlockCommand(state, tr, range, node);
                },
            }),

            // \author{Text} → docAuthor node
            new InputRule({
                find: /\\author\{([^}]*)\}$/,
                handler: ({ state, range, match }) => {
                    const { tr, schema } = state;
                    const text = match[1];
                    const nodeType = schema.nodes.docAuthor;
                    if (!nodeType) return null;

                    const content = text ? [schema.text(text)] : [];
                    const node = nodeType.create({}, content);
                    replaceBlockCommand(state, tr, range, node);
                },
            }),
        ];
    },
});

// ── Helpers ──────────────────────────────────────────────────────────────────

/**
 * Replace a block-level LaTeX command. If the command is the only content
 * in its paragraph, replace the entire paragraph; otherwise replace only
 * the matched range.
 */
function replaceBlockCommand(
    state: any,
    tr: any,
    range: { from: number; to: number },
    node: any,
) {
    const $from = state.doc.resolve(range.from);
    const textBefore = state.doc.textBetween(
        $from.start($from.depth),
        range.from,
    );

    if (textBefore.trim() === "") {
        // Command is the only content — replace the whole paragraph
        const paraStart = $from.start($from.depth) - 1;
        const paraEnd = $from.end($from.depth) + 1;
        tr.replaceWith(paraStart, paraEnd, node);
    } else {
        // Content before the command — just replace the matched text
        tr.replaceWith(range.from, range.to, node);
    }
}

/**
 * Convert \section{Text} (and variants) into a heading node.
 */
function sectionHandler(
    state: any,
    range: { from: number; to: number },
    match: RegExpMatchArray,
    level: number,
) {
    const { tr, schema } = state;
    const text = match[1];
    const headingType = schema.nodes.heading;
    if (!headingType) return null;

    const content = text ? [schema.text(text)] : [];
    const headingNode = headingType.create({ level }, content);

    const $from = state.doc.resolve(range.from);
    const textBefore = state.doc.textBetween(
        $from.start($from.depth),
        range.from,
    );

    if (textBefore.trim() === "") {
        const paraStart = $from.start($from.depth) - 1;
        const paraEnd = $from.end($from.depth) + 1;
        tr.replaceWith(paraStart, paraEnd, headingNode);
    } else {
        // Text before — delete command and insert heading after paragraph
        tr.delete(range.from, range.to);
        if (text) tr.insertText(text, range.from);
    }
}

/**
 * Convert \textbf{Text}, \textit{Text}, etc. into marked inline text.
 */
function inlineMarkHandler(
    state: any,
    range: { from: number; to: number },
    match: RegExpMatchArray,
    markName: string,
) {
    const { tr, schema } = state;
    const content = match[1];
    const markType = schema.marks[markName];
    if (!markType) return null;

    const mark = markType.create();
    const textNode = schema.text(content, [mark]);
    tr.replaceWith(range.from, range.to, textNode);
}
